import type {FC, PropsWithChildren} from 'react';
import clsx from 'clsx';

import {Container} from '@/modules/foundation/components/container';
import ImageFill from '@/modules/foundation/components/image/ImageFill';
import type {ImageWeb} from '@/modules/foundation/components/image/types';
import {Heading} from '@/modules/foundation/components/typography/heading';
import type {PortableTextOverrides} from '@/modules/foundation/sanity/portable-text';

type Props = PropsWithChildren & {
	heading?: string;
	imageWeb?: ImageWeb;
	imageAlignment?: 'left' | 'right';
};

export const heroWithSideImagePortableTextOverrides: PortableTextOverrides = {
	// callToActionLine: 'justify-center lg:justify-start',
	// p: 'text-balance',
};

export const HeroWithSideImage: FC<Props> = ({
	heading,
	imageWeb,
	children,
	imageAlignment = 'right',
}) => {
	const leftAlign = imageAlignment === 'left';
	return (
		<>
			<Container spacingBottom="normal" spacingTop="large">
				<div
					className={clsx('grid', 'lg:grid-cols-2', 'items-center', 'gap-4', 'lg:gap-20')}
				>
					<div className={clsx('lg:py-12', leftAlign && 'lg:order-2')}>
						<Heading spacing>{heading}</Heading>
						{children}
					</div>
					<div
						className={clsx(
							'relative',
							'aspect-[8/7]',
							leftAlign
								? 'rounded-[120px]'
								: 'rounded-[63%_37%_58%_42%_/_46%_40%_60%_54%]',
							'overflow-hidden',
						)}
					>
						{imageWeb && (
							<ImageFill
								priority // Images in page heads are usually part of the LCP
								src={imageWeb.image.src}
								width={imageWeb.image.width}
								height={imageWeb.image.height}
								hotspot={imageWeb.image.hotspot}
								alt={imageWeb.altText ?? ''}
							/>
						)}
					</div>
				</div>
			</Container>
			{leftAlign ? (
				<div
					className={clsx(
						'overflow-hidden',
						'absolute',
						'top-0',
						'left-0',
						'right-0',
						'bottom-0',
						'-z-50',
						'before:rounded-[70%_30%_50%_50%_/_30%_69%_31%_70%]',
						'before:absolute',
						'before:top-32',
						'before:left-1/2',
						'before:-translate-x-1/2',
						'before:h-screen',
						'before:w-[150vw]',
						'before:bg-gradient-to-b',
						'before:from-[#E0F6FF]',
						'before:to-white',
					)}
				/>
			) : (
				<div
					className={clsx(
						'overflow-hidden',
						'absolute',
						'top-0',
						'left-0',
						'right-0',
						'bottom-0',
						'-z-50',
						'before:transition-all',
						'before:rounded-[34%_66%_50%_50%_/_30%_20%_80%_70%]',
						'md:before:rounded-[34%_66%_50%_50%_/_30%_69%_31%_70%]',
						'before:absolute',
						'before:top-32',
						'before:left-1/2',
						'before:-translate-x-1/2',
						'before:h-screen',
						'before:w-[150vw]',
						'before:bg-gradient-to-b',
						'before:from-[#E0F6FF]',
						'before:to-white',
					)}
				/>
			)}
		</>
	);
};
